import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { TableLoader, ListLoader } from './CommonLoader';

// Determine if we're on a touch device
const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
const dndBackend = isTouchDevice ? TouchBackend : HTML5Backend;

const DraggableColumn = ({ column, index, moveColumn, hidden, onToggleHide }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'column',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'column',
    hover: (item) => {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <th
      ref={(node) => drag(drop(node))}
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-move ${
        isDragging ? 'opacity-50' : ''
      } ${hidden ? 'opacity-30' : ''}`}
    >
      <div className="flex items-center gap-2">
        {column.header || column.label}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleHide(column.key);
          }}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
        >
          {hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
      </div>
    </th>
  );
};

const ResponsiveDataTable = ({ columns: initialColumns, data, onRowClick, emptyMessage, loading, actions }) => {
  // Ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];
  
  const [columns, setColumns] = useState(initialColumns);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const moveColumn = (fromIndex, toIndex) => {
    const newColumns = [...columns];
    const [movedColumn] = newColumns.splice(fromIndex, 1);
    newColumns.splice(toIndex, 0, movedColumn);
    setColumns(newColumns);
  };

  const toggleHideColumn = (key) => {
    const newHiddenColumns = new Set(hiddenColumns);
    if (newHiddenColumns.has(key)) {
      newHiddenColumns.delete(key);
    } else {
      newHiddenColumns.add(key);
    }
    setHiddenColumns(newHiddenColumns);
  };

  // Mobile card view
  if (isMobile) {
    return (
      <div className="space-y-4">
        {loading ? (
          <ListLoader rows={5} />
        ) : safeData.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {emptyMessage || 'No data found'}
          </div>
        ) : (
          safeData.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="bg-white rounded-lg shadow p-4 space-y-3"
              onClick={() => onRowClick && onRowClick(row)}
              style={{ 
                borderLeft: '4px solid var(--color-primary)',
                cursor: onRowClick ? 'pointer' : 'default'
              }}
            >
              {columns
                .filter(col => !hiddenColumns.has(col.key))
                .map((column) => (
                  <div key={column.key} className="flex justify-between items-start">
                    <span className="text-sm font-medium text-gray-500">{column.header || column.label}:</span>
                    <span className="text-sm text-gray-900 text-right ml-2">
                      {column.render ? column.render(row) : row[column.key]}
                    </span>
                  </div>
                ))}
              {actions && (
                <div className="pt-2 border-t border-gray-100 flex justify-end gap-2">
                  {(() => {
                    const result = actions(row);
                    // Check if actions returns an array (new pattern) or JSX (old pattern)
                    if (Array.isArray(result)) {
                      return result.map((action, index) => (
                        <button
                          key={index}
                          onClick={action.onClick}
                          className={`p-2 rounded-lg transition-colors ${action.className || ''}`}
                          title={action.label}
                        >
                          <action.icon className="w-4 h-4" />
                        </button>
                      ));
                    }
                    // If it's JSX, render it directly
                    return result;
                  })()}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    );
  }

  // Desktop table view with drag and drop
  return (
    <DndProvider backend={dndBackend}>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-700">
            {safeData.length} {safeData.length === 1 ? 'item' : 'items'}
          </h3>
          <button
            onClick={() => setShowColumnSettings(!showColumnSettings)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Customize Columns
          </button>
        </div>

        {showColumnSettings && (
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-xs text-gray-600 mb-2">Drag columns to reorder. Click eye icon to hide/show.</p>
            <div className="flex flex-wrap gap-2">
              {columns.map((col) => (
                <button
                  key={col.key}
                  onClick={() => toggleHideColumn(col.key)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    hiddenColumns.has(col.key)
                      ? 'bg-gray-200 text-gray-500'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {col.header || col.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column, index) =>
                  !hiddenColumns.has(column.key) ? (
                    <DraggableColumn
                      key={column.key}
                      column={column}
                      index={index}
                      moveColumn={moveColumn}
                      hidden={hiddenColumns.has(column.key)}
                      onToggleHide={toggleHideColumn}
                    />
                  ) : null
                )}
                {actions && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={columns.filter(col => !hiddenColumns.has(col.key)).length + (actions ? 1 : 0)} className="py-4">
                    <TableLoader rows={5} columns={columns.filter(col => !hiddenColumns.has(col.key)).length} />
                  </td>
                </tr>
              ) : safeData.length === 0 ? (
                <tr>
                  <td colSpan={columns.filter(col => !hiddenColumns.has(col.key)).length + (actions ? 1 : 0)} className="text-center py-8 text-gray-500">
                    {emptyMessage || 'No data found'}
                  </td>
                </tr>
              ) : (
                safeData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="hover:bg-gray-50 transition-colors"
                    onClick={() => onRowClick && onRowClick(row)}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {columns
                      .filter(col => !hiddenColumns.has(col.key))
                      .map((column) => (
                        <td key={column.key} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {column.render ? column.render(row) : row[column.key]}
                        </td>
                      ))}
                    {actions && (
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const result = actions(row);
                          // Check if actions returns an array (new pattern) or JSX (old pattern)
                          if (Array.isArray(result)) {
                            return (
                              <div className="flex items-center gap-1">
                                {result.map((action, index) => (
                                  <button
                                    key={index}
                                    onClick={action.onClick}
                                    className={`p-1.5 rounded-lg transition-colors hover:bg-gray-100 ${action.className || ''}`}
                                    title={action.label}
                                  >
                                    <action.icon className="w-4 h-4" />
                                  </button>
                                ))}
                              </div>
                            );
                          }
                          // If it's JSX, render it directly
                          return result;
                        })()}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DndProvider>
  );
};

export default ResponsiveDataTable;